/**
 * oct20e LABOR (PR #13 governed remediation, ledger row 6a) — the ONE client read of a ticket's labor quantities.
 *
 * FD-2026-10-24-B: "Corrected timer-session labor is the authority for actual technician work." / "Payroll, clock
 * hours, actual technician labor and approved labor remain distinct." CORE-AJ :49: "Where no session ran, there is no
 * time." CORE-AL :42: "Unknown remains unknown."
 *
 * Displays used to read `tickets.actual_minutes` (nothing writes it for current work — oct20d-diagfix §2.1) or to sum
 * `ticket_jobs.actual_minutes` (0 when an episode is finalized without explicit allocations), and showed blank / 0.
 * `ticket_labor_minutes` (migration 20270212230000) answers, per ticket the caller may read:
 *   - actualLaborMinutes:   Σ stopped CORE-AJ session minutes with authorised labor corrections applied (NULL = unknown)
 *   - approvedLaborMinutes: the approved work expectation (CORE-BF/CORE-G) — never the scheduling duration
 *   - openSession:          a session is still running (its minutes are not counted yet)
 */
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface TicketLaborMinutes {
  ticketId: string;
  actualLaborMinutes: number | null;
  approvedLaborMinutes: number | null;
  openSession: boolean;
}

interface Row {
  ticket_id: string;
  actual_labor_minutes: number | null;
  approved_labor_minutes: number | null;
  open_session: boolean | null;
}

const MAX_PER_CALL = 500;

/** Reads the labor quantities of the given tickets (chunked; RLS decides what the caller sees). */
export async function fetchTicketLaborMinutes(ticketIds: ReadonlyArray<string>): Promise<Map<string, TicketLaborMinutes>> {
  const ids = Array.from(new Set(ticketIds.filter(Boolean)));
  const out = new Map<string, TicketLaborMinutes>();
  for (let i = 0; i < ids.length; i += MAX_PER_CALL) {
    const chunk = ids.slice(i, i + MAX_PER_CALL);
    // untyped: the generated client types predate this RPC (same pattern as other recent RPC callers)
    const { data, error } = await (supabase as any).rpc('ticket_labor_minutes', { p_ticket_ids: chunk });
    if (error) throw error;
    for (const r of (data ?? []) as Row[]) {
      out.set(r.ticket_id, {
        ticketId: r.ticket_id,
        actualLaborMinutes: r.actual_labor_minutes ?? null,
        approvedLaborMinutes: r.approved_labor_minutes ?? null,
        openSession: r.open_session === true,
      });
    }
  }
  return out;
}

/** React hook over fetchTicketLaborMinutes. Disabled (no request) when `enabled` is false or there are no ids. */
export function useTicketLaborMinutes(ticketIds: ReadonlyArray<string>, enabled = true) {
  const key = Array.from(new Set(ticketIds.filter(Boolean))).sort();
  return useQuery({
    queryKey: ['ticket-labor-minutes', key],
    queryFn: () => fetchTicketLaborMinutes(key),
    enabled: enabled && key.length > 0,
    staleTime: 30_000,
  });
}
